import { useState, useEffect, lazy, Suspense } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import DemoEnquiryModal from '../components/DemoEnquiryModal';
import { SHOT, ScreenshotFrame, PhoneShot, ShowcaseMedia } from '../components/ProductMedia';
import { DESIGN_COPY, customCopy } from '../utils/landingCopy';
import {
  MapPin, Users, ChevronDown, GraduationCap, Target, ArrowRight, Check, Phone, Mail, Zap,
  CreditCard, Users2, PlayCircle, Building2, BarChart3, Bell, BookOpenCheck, ShieldCheck,
  Trophy, Clock, MessageCircle, Fingerprint, Youtube, Instagram, Facebook, Linkedin,
  Twitter, Sparkles, CheckCircle2, Brain, Video, FileSpreadsheet, FileCheck,
  Palette, Bus, BedDouble, Library, Globe, PhoneCall, Wallet, IdCard,
  LayoutDashboard, Briefcase, Network
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = 'https://mycoachhub.in';
const DEFAULT_META_TITLE = 'CoachHub ERP: School, College & Coaching Institute Management Software';
const DEFAULT_META_DESCRIPTION = 'Modern ERP software for schools, colleges and coaching institutes. Manage admissions, fees, attendance, LMS, exams, communication and analytics in one platform.';
const DeferredPublicChatWidget = lazy(() => import('../components/PublicChatWidget'));

const normalizeExternalUrl = (value) => {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// Facts about the product shown in the hero
const HERO_FACTS = [
  { value: '20+', label: 'Modules' },
  { value: '₹100', label: 'Per student / year' },
  { value: '14 days', label: 'Free trial' },
];

const INSTITUTION_TYPES = [
  {
    key: 'school',
    title: 'School ERP',
    short: 'Schools',
    desc: 'A complete school ERP for admissions, student records, fee collection, attendance, exams, report cards and parent communication.',
    icon: GraduationCap,
    points: ['Admission pipeline & student records', 'Class-wise fee & attendance automation', 'Exams, report cards & admit cards', 'Parent app with instant alerts'],
    modules: ['Admissions', 'Fees', 'Attendance', 'Exams', 'Report Cards', 'Transport', 'Parent App'],
  },
  {
    key: 'college',
    title: 'College ERP',
    short: 'Colleges',
    desc: 'Centralise departments, batches, students, faculty workflows, payments, LMS content, certificates and performance visibility.',
    icon: Building2,
    points: ['Department-wise control', 'Faculty and student portals', 'Certificates & ID cards', 'Branch-wise analytics'],
    modules: ['Departments', 'LMS', 'Exams', 'Certificates', 'Fees', 'HR & Payroll', 'Analytics'],
  },
  {
    key: 'coaching',
    title: 'Coaching ERP',
    short: 'Coaching',
    desc: 'Grow enquiries, manage batches, collect fees, run tests, share study material and keep parents updated from one platform.',
    icon: Target,
    points: ['CRM with lead follow-up', 'Batch & timetable management', 'Online tests, DPPs & rankings', 'WhatsApp-ready communication'],
    modules: ['CRM', 'Batches', 'Online Exams', 'DPP', 'LMS', 'Fees', 'Merit Lists'],
  },
];

const DEMO_WORKFLOW = [
  { title: 'Capture', desc: 'An enquiry from the website lands straight in your admissions pipeline.' },
  { title: 'Convert', desc: 'Counsel, follow up and turn the lead into an admitted student in a click.' },
  { title: 'Operate', desc: 'Track fees, batches and biometric attendance from one owner dashboard.' },
  { title: 'Delight', desc: 'Parents get alerts, results and fee status on the app — automatically.' },
];

const FAQ_ITEMS = [
  {
    question: 'What is education ERP software?',
    answer: 'Institute management software helps schools, colleges and coaching centers manage admissions, students, fees, attendance, batches, exams, LMS and communication from one platform.',
  },
  {
    question: 'Is CoachHub suitable for schools, colleges and coaching institutes?',
    answer: 'Yes. Coaching institutes, schools and colleges use the same platform; you pick your institute type and get the right academic structure, with modules like transport and hostel when you need them.',
  },
  {
    question: 'How much does mycoachhub.in cost?',
    answer: 'Under the current limited-time offer the ERP costs ₹100 per student per year instead of ₹500, with all core modules included and a 14-day free trial. AI credits and a few add-on modules are billed separately.',
  },
  {
    question: 'Does it support biometric attendance and WhatsApp alerts?',
    answer: 'Yes. eSSL and ZKTeco (ADMS) and Hikvision devices sync with CoachHub, and parents get WhatsApp messages on punch in, punch out and when the child is absent, through approved Meta WhatsApp templates.',
  },
  {
    question: 'Do students and parents get mobile access?',
    answer: 'Yes. Students and parents get their own app login to see attendance, fees, results, report cards and notices; teachers and owners use the same platform on phone and computer.',
  },
  {
    question: 'Can you move our data from Excel or our old software?',
    answer: 'Yes. Our team helps import students, batches, fee records and staff from Excel sheets or your previous software so you can start without re-entering data.',
  },
  {
    question: 'Is our institute data secure?',
    answer: 'Every staff member gets role-based access so they only see what they need, changes to fees and marks are kept in an audit log, and owners can download a full backup of their data with the Data Backup module.',
  },
  {
    question: 'Can we get our own institute logo, app name and color combination on the mobile app?',
    answer: 'Yes. CoachHub offers white-label customization: your mobile app and portals can carry your institute logo, colors and name.',
  },
];

const ROLE_ICONS = {
  'Institute Owner': Building2,
  'Teacher': GraduationCap,
  'Student': BookOpenCheck,
  'Parent': Users,
};

/* ─── Hero visual: real fees screen + parent app ─── */
const HeroProductShot = () => (
  <div className="relative pb-8 pr-10 sm:pb-12 sm:pr-24">
    <ScreenshotFrame
      file="fees.webp"
      alt="CoachHub fees screen: total expected, collected, pending balance and every student's fee status"
      eager
    />
    <PhoneShot
      file="app-parent-fees.webp"
      alt="CoachHub parent app fees: total, paid, balance and the next due installment"
      eager
      className="absolute right-0 bottom-0 w-[30%] max-w-[200px]"
    />
    <div className="absolute left-3 -bottom-1 sm:left-6 sm:bottom-2 flex items-center gap-2.5 rounded-2xl bg-white/95 backdrop-blur px-3 py-2.5 shadow-2xl shadow-black/30 site-pop-in">
      <span className="w-8 h-8 rounded-lg bg-[#0F6E64] flex items-center justify-center flex-shrink-0">
        <MessageCircle className="w-4 h-4 text-white" />
      </span>
      <span>
        <span className="block text-[13px] font-bold text-brand-ink leading-tight">Fee reminder sent</span>
        <span className="block text-[11px] text-slate-500">On WhatsApp, with pay link</span>
      </span>
    </div>
  </div>
);

/* ─── Mobile app section: real app screens per role ─── */
const APP_SCREENS = {
  Student: [
    { file: 'app-student-home.webp', alt: 'CoachHub student app home: what needs attention today, exams and fees' },
    { file: 'app-student-id.webp', alt: 'CoachHub student app digital ID card with QR' },
  ],
  Parent: [
    { file: 'app-parent-home.webp', alt: 'CoachHub parent app dashboard: attendance, average score and pending fees' },
    { file: 'app-parent-results.webp', alt: 'CoachHub parent app exam results with marks and percentage' },
  ],
};
// Owner and teacher screens were not captured, so those tabs show the parent and student apps
const DEFAULT_APP_SCREENS = [
  { file: 'app-parent-home.webp', alt: 'CoachHub parent app dashboard: attendance, average score and pending fees' },
  { file: 'app-student-home.webp', alt: 'CoachHub student app home: what needs attention today, exams and fees' },
];

const AppPhones = ({ activeRole }) => {
  const [front, back] = APP_SCREENS[activeRole] || DEFAULT_APP_SCREENS;
  return (
    <div className="relative mx-auto w-full max-w-[420px] h-[460px] sm:h-[560px]">
      <div className="absolute -inset-6 bg-brand-gold/15 rounded-[48px] blur-3xl" aria-hidden="true" />
      <PhoneShot {...back} className="absolute left-0 top-10 w-[46%] -rotate-6 opacity-90" />
      <PhoneShot {...front} className="absolute right-2 top-0 w-[52%] rotate-3" />
    </div>
  );
};

const SHOWCASES = [
  {
    id: 'attendance',
    title: 'Biometric attendance with WhatsApp alerts',
    subtitle: 'The student punches in, and the parent knows within seconds. No calls to the front desk.',
    bullets: ['eSSL, ZKTeco and Hikvision devices', 'Punch-in and punch-out alerts', 'Absent alert at a time you set', 'Manual marking when needed', 'Staff attendance on the same device', 'Daily trend and class-wise reports'],
    media: {
      gif: {
        file: 'biometric-whatsapp.gif',
        alt: 'Animation: a student punches in on the biometric machine and the parent instantly gets WhatsApp messages for punch in, punch out and absent',
        caption: 'Punch in, punch out and absent: parent knows instantly',
      },
    },
  },
  {
    id: 'fees',
    title: 'Fees that follow themselves up',
    subtitle: 'Reminders go out before and after the due date with a pay link, and the receipt reaches the parent the moment fees are paid.',
    bullets: ['Installments, discounts and late fees', 'WhatsApp reminders with pay link', 'Cash, UPI, cheque or online payment', 'Instant receipt link to the parent', 'Pending list and defaulter aging', 'AI forecast of late payments'],
    media: {
      gif: {
        file: 'fee-reminder.gif',
        alt: 'Animation: the parent gets a WhatsApp fee reminder with a pay link, pays, receives the receipt, and an overdue reminder goes to another parent',
        caption: 'Reminder, payment, receipt: all automatic',
      },
    },
  },
  {
    id: 'crm',
    title: 'Every enquiry called, every lead followed up',
    subtitle: 'Leads from ads, your website and walk-ins land in one CRM, and the AI agent calls them while they are still interested.',
    bullets: ['Facebook and Instagram lead ads', 'Website and walk-in enquiries', 'AI calls in Hindi and English', 'Recording, transcript and summary', "Today's follow-ups on top", 'Lead source and conversion reports'],
    media: {
      gif: {
        file: 'ai-calling.gif',
        alt: 'Animation: a new enquiry arrives, the AI agent calls in Hindi, and the call summary shows a campus visit booked',
        caption: 'New lead called by AI, campus visit booked',
      },
    },
  },
  {
    id: 'exams',
    title: 'Tests, ranks and report cards parents understand',
    subtitle: 'Publish a result and the rank list builds itself; parents get a report card that shows strengths, weak topics and what to do next.',
    bullets: ['AI question paper generator', 'Online, OMR or offline marks', 'Rank list with tie-break rules', 'AI report card link on WhatsApp', 'Admit cards with QR', 'Batch-wise performance comparison'],
    media: {
      gif: {
        file: 'result-report-card.gif',
        alt: 'Animation: a test result is published, the rank list is generated and the parent gets the AI report card link on WhatsApp',
        caption: 'Result published, rank list ready, report card to parent',
      },
    },
  },
];

// Every module at a glance (screens for most of them are in the product tour)
const MODULE_GRID = [
  { icon: LayoutDashboard, title: 'Owner dashboard', desc: 'The whole institute at a glance' },
  { icon: Users, title: 'Students', desc: 'Admission to TC, with family' },
  { icon: Target, title: 'Enquiry CRM', desc: 'Lead to admission' },
  { icon: PhoneCall, title: 'AI calling', desc: 'Calls every new lead' },
  { icon: Fingerprint, title: 'Biometric', desc: 'Punch alerts on WhatsApp' },
  { icon: CreditCard, title: 'Fees', desc: 'Installments, reminders, receipts' },
  { icon: Wallet, title: 'Accounts', desc: 'Expenses, ledger, cash flow' },
  { icon: GraduationCap, title: 'Academics', desc: 'Batches, timetable, LMS' },
  { icon: FileCheck, title: 'Tests & OMR', desc: 'Rank lists automatically' },
  { icon: Brain, title: 'AI tools', desc: 'Papers, analysis, report cards' },
  { icon: Video, title: 'Live classes', desc: 'Video classes and DPPs' },
  { icon: MessageCircle, title: 'WhatsApp & SMS', desc: 'Alerts, chat, campaigns' },
  { icon: Briefcase, title: 'HRMS & payroll', desc: 'Staff, leaves, salary slips' },
  { icon: IdCard, title: 'Documents', desc: 'ID cards, admit cards, TC' },
  { icon: BarChart3, title: 'Reports', desc: 'Growth, attendance, dues' },
  { icon: Network, title: 'Multi-branch', desc: 'Every branch in one login' },
  { icon: Bus, title: 'Transport', desc: 'Routes and bus fees' },
  { icon: BedDouble, title: 'Hostel', desc: 'Rooms, mess, gate pass' },
  { icon: Library, title: 'Library & dress', desc: 'Issue, fines, uniform stock' },
  { icon: Globe, title: 'Online profile', desc: 'Your page on mycoachhub.in' },
];

/* ─── Product tour: every module as it really looks ─── */
const PRODUCT_TOUR = [
  {
    key: 'fees',
    label: 'Fees & Accounts',
    icon: CreditCard,
    shots: [
      { file: 'payment-history.webp', width: 1344, height: 840, title: 'Payment history', desc: 'Every receipt by cash, UPI, cheque or online link, ready to export.' },
      { file: 'fee-forecast.webp', width: 1344, height: 840, title: 'AI fee forecast', desc: 'See which dues are likely to come late over the next 30, 60 and 90 days.' },
      { file: 'finance-hub.webp', width: 1344, height: 840, title: 'Finance hub', desc: 'Collection, expenses and cash flow month by month.' },
      { file: 'general-ledger.webp', width: 1344, height: 840, title: 'General ledger', desc: 'Chart of accounts and journal entries, with Tally export.' },
    ],
  },
  {
    key: 'attendance',
    label: 'Attendance',
    icon: Fingerprint,
    shots: [
      { file: 'attendance-analytics.webp', title: 'Attendance analytics', desc: 'Daily trend, attendance rate and class-wise comparison, from biometric or manual marking.' },
    ],
  },
  {
    key: 'academics',
    label: 'Academics & Exams',
    icon: GraduationCap,
    shots: [
      { file: 'batch-performance.webp', title: 'Batch performance', desc: 'Compare batches on attendance, exam scores and fees on one screen.' },
      { file: 'timetable.webp', title: 'Timetable', desc: 'Week grid for every class, with teacher substitution.' },
      { file: 'video-classes.webp', width: 1344, height: 840, title: 'Video classes', desc: 'Live, upcoming and completed classes in one list.' },
      { file: 'daily-practice.webp', width: 1344, height: 840, title: 'Daily practice papers', desc: 'Assign DPPs with due dates and track submissions and marks.' },
      { file: 'id-cards.webp', title: 'ID cards', desc: 'Pick students, preview and print ID cards with QR verification.' },
    ],
  },
  {
    key: 'admissions',
    label: 'Admissions & AI calling',
    icon: Target,
    shots: [
      { file: 'crm-leads.webp', width: 1344, height: 840, title: 'Lead CRM', desc: 'New, in-progress and converted leads, with today\'s follow-ups on top.' },
      { file: 'ai-calling-dashboard.webp', title: 'AI calling agent', desc: 'Calls made, answered and completed, with campaign performance.' },
    ],
  },
  {
    key: 'communication',
    label: 'WhatsApp & alerts',
    icon: MessageCircle,
    shots: [
      { file: 'whatsapp-chat.webp', width: 1344, height: 840, title: 'WhatsApp live chat', desc: 'One inbox for leads, students and parents.' },
      { file: 'fee-reminder-rules.webp', width: 1344, height: 840, title: 'Automatic fee reminders', desc: 'Decide when reminders go out and on which channel.' },
    ],
  },
  {
    key: 'people',
    label: 'Students & Staff',
    icon: Users,
    shots: [
      { file: 'students.webp', title: 'Students', desc: 'Every student with class, roll number, family and documents.' },
      { file: 'hrms.webp', title: 'Staff directory', desc: 'Teachers and staff with roles, departments and logins.' },
      { file: 'student-growth.webp', title: 'Student growth', desc: 'Monthly admissions and class-wise enrollment.' },
    ],
  },
  {
    key: 'campus',
    label: 'Transport, Hostel & Library',
    icon: Building2,
    shots: [
      { file: 'transport.webp', title: 'Transport', desc: 'Vehicles, routes, stops and transport fees.' },
      { file: 'hostel.webp', title: 'Hostel', desc: 'Rooms, beds, allocation, mess and gate passes.' },
      { file: 'library.webp', title: 'Library', desc: 'Books, issue and return, overdue fines.' },
    ],
  },
];

const COMPARISON_ROWS = [
  {
    feature: 'Attendance',
    icon: Fingerprint,
    oldWay: 'Paper registers and roll-calls. Parents call to ask whether their child reached on time.',
    coachHubWay: 'The biometric machine marks attendance and the parent gets a WhatsApp message on punch in, punch out or absence.',
    winnerNote: 'Parents know without calling you',
  },
  {
    feature: 'Fee collection',
    icon: CreditCard,
    oldWay: 'Reminder calls to every parent, paper receipts and a register that never matches the bank.',
    coachHubWay: 'Reminders with a pay link go out on their own, receipts reach the parent instantly and the pending list updates itself.',
    winnerNote: 'No more awkward reminder calls',
  },
  {
    feature: 'Admission enquiries',
    icon: Users2,
    oldWay: 'Leads written in a diary; follow-ups get forgotten and enquiries go cold.',
    coachHubWay: 'Ad, website and walk-in leads land in one CRM, and the AI agent calls new leads right away.',
    winnerNote: 'No enquiry left uncalled',
  },
  {
    feature: 'Tests and results',
    icon: Trophy,
    oldWay: 'Hours typing papers, marks sent as photos on WhatsApp, ranks worked out in Excel.',
    coachHubWay: 'AI drafts the paper, the rank list builds itself and parents get an AI report card link.',
    winnerNote: 'Parents see progress, not just marks',
  },
  {
    feature: 'Accounts',
    icon: Wallet,
    oldWay: 'Fees, rent, salaries and cash kept in separate diaries and spreadsheets.',
    coachHubWay: 'Fees, expenses, ledger and cash flow in one place, ready for your CA.',
    winnerNote: 'Know your profit any day',
  },
  {
    feature: 'Multiple branches',
    icon: Building2,
    oldWay: 'Calling every branch manager in the evening for collection and attendance numbers.',
    coachHubWay: 'One login shows every branch, with branch-wise roles and reports.',
    winnerNote: 'Every branch from your phone',
  },
];

/* ─── Small shared pieces ─── */
const Eyebrow = ({ children, dark = false }) => (
  <p className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] mb-4 ${dark ? 'text-brand-gold' : 'text-brand-gold-hover'}`}>
    <span className="w-6 h-px bg-current" /> {children}
  </p>
);

const goldBtn = 'group inline-flex items-center justify-center gap-2 bg-brand-gold hover:bg-brand-gold-hover text-brand-ink font-bold px-7 py-4 rounded-xl transition-all hover:-translate-y-0.5 shadow-[0_12px_32px_-12px_rgba(227,162,26,0.8)]';
const ghostBtnDark = 'inline-flex items-center justify-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] border border-white/20 text-white font-semibold px-7 py-4 rounded-xl backdrop-blur transition-colors';

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeAppRole, setActiveAppRole] = useState('Student');
  const [activeType, setActiveType] = useState(INSTITUTION_TYPES[2].key);
  const [showDeferredUi, setShowDeferredUi] = useState(false);
  const [studentCount, setStudentCount] = useState(300);

  // Demo Modal & Explorer States
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [demoRequirement, setDemoRequirement] = useState('');
  const [tourKey, setTourKey] = useState(PRODUCT_TOUR[0].key);
  const [tourShot, setTourShot] = useState(0);
  const activeTour = PRODUCT_TOUR.find((t) => t.key === tourKey) || PRODUCT_TOUR[0];
  const activeTourShot = activeTour.shots[tourShot] || activeTour.shots[0];

  // On-Page Demo Form State
  const [onPageForm, setOnPageForm] = useState({
    name: '',
    phone: '',
    institute_name: '',
    city: '',
    institute_type: 'Coaching',
    student_count: '100 – 300',
    primary_requirement: 'Biometric Attendance & Instant WhatsApp Alerts',
    notes: '',
  });
  const [onPageSubmitting, setOnPageSubmitting] = useState(false);
  const [onPageSubmitted, setOnPageSubmitted] = useState(false);

  const openDemoModal = (req = '') => {
    setDemoRequirement(req);
    setDemoModalOpen(true);
  };

  const handleOnPageSubmit = async (e) => {
    e.preventDefault();
    if (!onPageForm.name.trim() || !onPageForm.phone.trim()) {
      toast.error('Please enter your name and WhatsApp number');
      return;
    }
    setOnPageSubmitting(true);
    try {
      await axios.post(`${API}/marketplace/demo-enquiry`, onPageForm);
      setOnPageSubmitted(true);
      toast.success('Demo request submitted successfully!');
    } catch {
      setOnPageSubmitted(true);
    } finally {
      setOnPageSubmitting(false);
    }
  };

  // CMS Content State
  const [cmsContent, setCmsContent] = useState(null);

  useEffect(() => {
    axios.get(`${API}/marketplace/landing-content`)
      .then(r => setCmsContent(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || navigator.userAgent === 'ReactSnap') return undefined;

    const revealDeferredUi = () => setShowDeferredUi(true);
    if ('requestIdleCallback' in window) {
      const deferredId = window.requestIdleCallback(revealDeferredUi, { timeout: 3000 });
      return () => window.cancelIdleCallback?.(deferredId);
    }

    const timeoutId = window.setTimeout(revealDeferredUi, 1500);
    return () => window.clearTimeout(timeoutId);
  }, []);

  // Navbar links like "/#pricing" arrive here from other pages: scroll once the section exists.
  useEffect(() => {
    if (!location.hash) return undefined;
    const id = decodeURIComponent(location.hash.slice(1));
    let tries = 0;
    const timer = window.setInterval(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.clearInterval(timer);
      } else if (++tries > 30) {
        window.clearInterval(timer);
      }
    }, 100);
    return () => window.clearInterval(timer);
  }, [location.hash]);

  const mobileApp = cmsContent?.mobile_app || {};
  // Hero text set in Landing CMS > Hero; empty fields keep the designed copy
  const heroTitle = customCopy(cmsContent, 'hero_title');
  const heroSubtitle = customCopy(cmsContent, 'hero_subtitle') || DESIGN_COPY.hero_subtitle;
  const trialCta = customCopy(cmsContent, 'hero_cta_primary') || DESIGN_COPY.hero_cta_primary;
  const demoCta = customCopy(cmsContent, 'hero_cta_secondary') || DESIGN_COPY.hero_cta_secondary;
  const activeInstitution = INSTITUTION_TYPES.find((t) => t.key === activeType) || INSTITUTION_TYPES[0];
  const appRoles = mobileApp.roles || [
    { role: 'Institute Owner', features: ['Custom branded app with your logo & colors', 'Live revenue, fee collections & admissions', 'Push announcements & automated WhatsApp alerts', 'Multi-branch oversight & analytics on mobile'] },
    { role: 'Teacher', features: ['Mark attendance with one tap', 'View assigned batches & students', 'Track exam results & scores', 'Get instant notifications'] },
    { role: 'Student', features: ['Browse courses & study material', 'View exam results & scores', 'Track attendance & progress', 'Check fee status & pay online'] },
    { role: 'Parent', features: ['Monitor child\'s academic progress', 'Get attendance alerts instantly', 'View fee status & payment history', 'Track exam results & rankings'] },
  ];

  const footerConfig = cmsContent?.footer || {};
  const contactEmail = footerConfig.email || 'contact@mycoachhub.in';
  const contactPhone = footerConfig.phone || '+91 6260129453';
  const whatsappNumber = contactPhone.replace(/\D/g, '');
  const footerSocialLinks = [
    { key: 'youtube', label: 'YouTube', href: normalizeExternalUrl(footerConfig.youtube_url), icon: Youtube },
    { key: 'x', label: 'X', href: normalizeExternalUrl(footerConfig.x_url), icon: Twitter },
    { key: 'linkedin', label: 'LinkedIn', href: normalizeExternalUrl(footerConfig.linkedin_url), icon: Linkedin },
    { key: 'instagram', label: 'Instagram', href: normalizeExternalUrl(footerConfig.instagram_url), icon: Instagram },
    { key: 'facebook', label: 'Facebook', href: normalizeExternalUrl(footerConfig.facebook_url), icon: Facebook },
  ].filter((link) => link.href);
  const organizationSameAs = footerSocialLinks.map((link) => link.href);
  const twitterHandle = (() => {
    const xUrl = footerSocialLinks.find((link) => link.key === 'x')?.href || '';
    const match = xUrl.match(/(?:twitter\.com|x\.com)\/([A-Za-z0-9_]+)/i);
    return match ? `@${match[1]}` : '';
  })();

  return (
    <div className="site-public min-h-screen bg-white">
      <Helmet>
        <html lang="en-IN" />
        <title>{DEFAULT_META_TITLE}</title>
        <meta name="description" content={DEFAULT_META_DESCRIPTION} />
        <meta name="keywords" content="school ERP software, college ERP software, coaching institute software, institute management software, education ERP India, student management system, fee management software, LMS for institutes, attendance management software, exam management software" />
        <link rel="canonical" href={SITE_URL} />
        <link rel="alternate" hrefLang="en-IN" href={SITE_URL} />
        <link rel="alternate" hrefLang="x-default" href={SITE_URL} />
        <meta name="robots" content="index, follow" />
        <meta name="geo.region" content="IN" />
        <meta name="geo.country" content="India" />
        <meta name="language" content="English" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="CoachHub" />
        <meta property="og:title" content={DEFAULT_META_TITLE} />
        <meta property="og:description" content={DEFAULT_META_DESCRIPTION} />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:image" content={`${SITE_URL}/icons/og-default.png`} />
        <meta property="og:image:alt" content="CoachHub school, college and coaching institute ERP dashboard preview" />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}
        <meta name="twitter:title" content={DEFAULT_META_TITLE} />
        <meta name="twitter:description" content={DEFAULT_META_DESCRIPTION} />
        <meta name="twitter:image" content={`${SITE_URL}/icons/og-default.png`} />

        {/* JSON-LD: SoftwareApplication — Google rich results ke liye */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "CoachHub",
          "applicationCategory": "EducationApplication",
          "applicationSubCategory": "School, College and Institute Management Software",
          "operatingSystem": "Web, Android, iOS",
          "url": SITE_URL,
          "description": "Complete ERP software for schools, colleges and coaching institutes in India. Manage students, fees, batches, attendance, exams, LMS, AI tools and more.",
          "offers": {
            "@type": "Offer",
            "price": "100",
            "priceCurrency": "INR",
            "description": "Per student per year under the current offer, with a 14-day free trial"
          },
          "featureList": [
            "Student Management",
            "Fee Management",
            "Batch & Timetable Management",
            "Attendance Tracking",
            "Online Exams",
            "LMS - Learning Management System",
            "AI Exam Generator",
            "WhatsApp Notifications",
            "Performance Reports",
            "Certificate Generator"
          ],
          "inLanguage": ["en", "hi"],
          "provider": {
            "@type": "Organization",
            "name": "CoachHub",
            "url": SITE_URL
          }
        })}</script>

        {/* JSON-LD: Organization */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "CoachHub",
          "url": SITE_URL,
          "logo": `${SITE_URL}/icons/icon-192x192.png`,
          "description": "Modern ERP software for schools, colleges and coaching institutes — manage students, fees, exams, attendance, LMS and communication.",
          "sameAs": organizationSameAs,
          "areaServed": "IN",
          "knowsAbout": ["School ERP", "College ERP", "Coaching Institute Management", "Education Technology", "Student Management System"],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer support",
            "availableLanguage": ["English", "Hindi"]
          }
        })}</script>

        {/* JSON-LD: WebSite */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "CoachHub",
          "url": SITE_URL,
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${SITE_URL}/coaching-in-{search_term_string}`
            },
            "query-input": "required name=search_term_string"
          }
        })}</script>

        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQ_ITEMS.map((item) => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.answer,
            },
          })),
        })}</script>
      </Helmet>

      <Navbar />

      <main id="main-content">
      {/* ─── Hero ─── */}
      <section className="relative bg-brand-navy text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/40 via-transparent to-brand-ink/70" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 pt-8 pb-10 md:pt-12 md:pb-14 lg:pt-14 lg:pb-16">
          <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-10 xl:gap-14 items-center">
            {/* Left: message + CTAs */}
            <div className="text-center lg:text-left max-w-2xl mx-auto lg:mx-0">
              <div className="inline-flex items-center gap-2 border border-brand-gold/50 text-brand-gold text-xs sm:text-sm font-semibold px-4 py-1.5 rounded-full mb-5 bg-brand-gold/10 backdrop-blur-sm">
                <Sparkles className="w-3.5 h-3.5 text-brand-gold" /> Made in India for coaching institutes & schools
              </div>
              <h1 className="font-cabinet text-[40px] sm:text-6xl lg:text-[58px] xl:text-[66px] font-black leading-[0.98] tracking-tight mb-5 text-balance">
                {heroTitle || <>Run your institute like a <span className="text-brand-gold">premium brand.</span></>}
              </h1>
              <p className="text-base md:text-lg text-white/75 mb-6 leading-relaxed">
                {heroSubtitle}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-6">
                <button
                  type="button"
                  onClick={() => openDemoModal('All-in-One Institute ERP')}
                  className={goldBtn}
                  data-testid="hero-demo-cta-btn"
                >
                  <Sparkles className="w-4 h-4 text-brand-ink" />
                  {demoCta} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
                {user ? (
                  <button type="button" onClick={() => navigate('/dashboard')} className={ghostBtnDark} data-testid="hero-dashboard-btn">
                    Go to Dashboard <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <Link to="/register" data-testid="hero-trial-btn" className={ghostBtnDark}>
                    {trialCta}
                  </Link>
                )}
                <a
                  href={`https://wa.me/916260129453?text=${encodeURIComponent('Hi CoachHub team, I am an institute owner and would like a live demo of your software.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366]/20 hover:bg-[#25D366]/30 border border-[#25D366]/40 text-[#25D366] font-semibold px-5 py-4 rounded-xl backdrop-blur transition-colors text-sm"
                  data-testid="hero-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp Us
                </a>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-xs text-white/60 pt-2 border-t border-white/10">
                <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-brand-gold" /> 14-day free trial</span>
                <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-brand-gold" /> Zero setup fee</span>
                <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-brand-gold" /> Free student data migration</span>
                <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-brand-gold" /> Phone & computer</span>
              </div>
            </div>

            {/* Right: stats, then the product illustration */}
            <div className="w-full max-w-2xl mx-auto lg:max-w-none flex flex-col gap-4">
              <div className="grid grid-cols-3 divide-x divide-white/10 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm" data-testid="hero-stats">
                {HERO_FACTS.map((stat) => (
                  <div key={`${stat.label}-${stat.value}`} className="px-2 py-4 sm:py-5 text-center">
                    <p className="font-cabinet text-2xl sm:text-4xl xl:text-[42px] font-black text-white tracking-tight leading-none">{stat.value}</p>
                    <p className="text-[10px] sm:text-xs text-white/55 mt-2 uppercase tracking-[0.16em]">{stat.label}</p>
                  </div>
                ))}
              </div>
              <HeroProductShot />
              <div className="hidden sm:grid sm:grid-cols-3 gap-2">
                {[
                  { icon: Fingerprint, label: 'Biometric sync' },
                  { icon: MessageCircle, label: 'Instant parent alerts' },
                  { icon: BarChart3, label: 'Auto daily reports' },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-white/10 bg-white/[0.04] px-2 py-2.5 text-[13px] xl:text-sm text-white/80">
                    <Icon className="w-4 h-4 text-brand-gold flex-shrink-0" /> {label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Feature showcases ─── */}
      <section id="features" className="scroll-mt-24 bg-brand-mist pt-16 md:pt-20 pb-6 md:pb-10 px-4 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-4 md:mb-6">
            <Eyebrow>Watch it work</Eyebrow>
            <h2 className="font-cabinet text-3xl sm:text-4xl md:text-5xl font-black text-brand-ink leading-[1.02] tracking-tight">
              The four jobs owners worry about, <span className="text-brand-gold-hover">done automatically.</span>
            </h2>
            <p className="text-sm md:text-base text-slate-600 mt-3 max-w-2xl mx-auto">
              Attendance, fees, admissions and results: each one below is a short live demo of the real workflow.
            </p>
          </div>

          {SHOWCASES.map(({ id, title, subtitle, bullets, media }, idx) => {
            const mockupFirst = idx % 2 === 1;
            return (
              <div
                key={id}
                id={id}
                className="scroll-mt-24 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 xl:gap-20 items-center py-10 md:py-14 border-b border-slate-200/70 last:border-0"
                data-testid={`showcase-${id}`}
              >
                <div className={`max-w-xl mx-auto lg:mx-0 w-full min-w-0 ${mockupFirst ? 'lg:order-2' : ''}`}>
                  <p className="font-cabinet text-sm font-black text-brand-gold-hover mb-3">{String(idx + 1).padStart(2, '0')}</p>
                  <h3 className="font-cabinet text-3xl sm:text-4xl xl:text-[44px] font-black text-brand-ink leading-[1.05] tracking-tight mb-4">{title}</h3>
                  <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-6">{subtitle}</p>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                    {bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2.5 text-[15px] leading-snug text-brand-ink">
                        <Check className="w-5 h-5 text-brand-gold-hover flex-shrink-0 mt-0.5" strokeWidth={3} /> {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`relative w-full min-w-0 max-w-2xl mx-auto lg:max-w-none ${mockupFirst ? 'lg:order-1' : ''}`}>
                  <div className={`absolute top-6 bottom-0 ${mockupFirst ? '-left-3 right-10' : 'left-10 -right-3'} rounded-[32px] bg-brand-gold/20 ${mockupFirst ? '-rotate-2' : 'rotate-2'}`} aria-hidden="true" />
                  <div className="relative"><ShowcaseMedia {...media} /></div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ─── Product tour: real screens of every module ─── */}
      <section id="product-tour" className="scroll-mt-24 py-16 md:py-20 lg:py-24 px-4 bg-white border-b border-slate-200/80" data-testid="product-tour">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <Eyebrow>Product tour</Eyebrow>
            <h2 className="font-cabinet text-3xl sm:text-4xl md:text-5xl font-black text-brand-ink leading-[1.02] tracking-tight">
              See the real software, <span className="text-brand-gold-hover">screen by screen.</span>
            </h2>
            <p className="text-base md:text-lg text-slate-600 mt-4 leading-relaxed">
              No drawings or mock-ups: these are actual CoachHub screens from a demo institute. Pick a module and look around.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-10 items-start">
            <div className="flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0 -mx-4 px-4 lg:mx-0 lg:px-0" role="tablist" aria-label="Modules">
              {PRODUCT_TOUR.map(({ key, label, icon: Icon, shots }) => {
                const isActive = tourKey === key;
                return (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => { setTourKey(key); setTourShot(0); }}
                    data-testid={`tour-tab-${key}`}
                    className={`flex items-center gap-3 whitespace-nowrap rounded-xl px-4 py-3 text-left text-sm font-semibold transition-all ${
                      isActive
                        ? 'bg-brand-ink text-white shadow-lg'
                        : 'bg-brand-mist text-brand-ink hover:bg-slate-200/70'
                    }`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-brand-gold' : 'text-brand-gold-hover'}`} />
                    <span className="flex-1">{label}</span>
                    <span className={`text-xs ${isActive ? 'text-white/60' : 'text-slate-400'}`}>{shots.length}</span>
                  </button>
                );
              })}
            </div>

            <div className="min-w-0" role="tabpanel">
              <ScreenshotFrame
                key={activeTourShot.file}
                file={activeTourShot.file}
                width={activeTourShot.width || 1600}
                height={activeTourShot.height || 1000}
                alt={`CoachHub ${activeTourShot.title}: ${activeTourShot.desc}`}
                className="site-pop-in"
              />
              <div className="mt-5 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="max-w-xl">
                  <p className="font-cabinet text-xl sm:text-2xl font-black text-brand-ink">{activeTourShot.title}</p>
                  <p className="text-sm sm:text-base text-slate-600 mt-1 leading-relaxed">{activeTourShot.desc}</p>
                </div>
                {activeTour.shots.length > 1 && (
                  <div className="flex gap-2 flex-wrap">
                    {activeTour.shots.map((shot, i) => (
                      <button
                        key={shot.file}
                        type="button"
                        onClick={() => setTourShot(i)}
                        aria-label={`Show ${shot.title}`}
                        aria-pressed={i === tourShot}
                        className={`w-20 sm:w-24 rounded-lg overflow-hidden border-2 transition-all ${i === tourShot ? 'border-brand-gold shadow-md' : 'border-slate-200 opacity-70 hover:opacity-100'}`}
                      >
                        <img src={SHOT(shot.file)} alt="" width={shot.width || 1600} height={shot.height || 1000} loading="lazy" decoding="async" className="block w-full h-auto" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link to="/register" className={goldBtn} data-testid="tour-trial-btn">
                  Try it with your own data <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <button
                  type="button"
                  onClick={() => openDemoModal(`Product tour: ${activeTour.label}`)}
                  className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-brand-ink text-brand-ink font-semibold px-7 py-4 rounded-xl transition-colors"
                >
                  <PlayCircle className="w-4 h-4" /> Get a live demo
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-4">Screens from a demo institute; phone numbers and emails are blurred.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── All modules at a glance ─── */}
      <section id="all-modules" className="scroll-mt-24 py-16 md:py-20 px-4 bg-brand-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-60" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-4 lg:gap-10 items-end mb-10">
            <div>
              <Eyebrow dark>Complete ERP</Eyebrow>
              <h2 className="font-cabinet text-3xl sm:text-4xl md:text-5xl font-black leading-[1.02] tracking-tight">
                20 modules, <span className="text-brand-gold">one login.</span>
              </h2>
            </div>
            <p className="text-base md:text-lg text-white/65 leading-relaxed lg:justify-self-end max-w-xl">
              Start with fees and attendance, switch on the rest when you need it. Everything shares the same student record.
            </p>
          </div>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
            {MODULE_GRID.map(({ icon: Icon, title, desc }) => (
              <li key={title} className="bg-brand-ink p-4 sm:p-5 flex items-start gap-3 hover:bg-brand-graphite transition-colors">
                <span className="w-9 h-9 rounded-lg border border-white/10 bg-white/[0.04] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-brand-gold" />
                </span>
                <span className="min-w-0">
                  <span className="block font-bold text-white text-sm leading-snug">{title}</span>
                  <span className="block text-xs text-white/55 leading-snug mt-0.5">{desc}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ─── Why Owners Switch (Comparison Matrix) ─── */}
      <section id="why-switch" className="scroll-mt-24 py-16 md:py-20 lg:py-24 px-4 bg-white border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow>Before and after</Eyebrow>
            <h2 className="font-cabinet text-3xl sm:text-4xl md:text-5xl font-black text-brand-ink leading-[1.02] tracking-tight">
              From registers and Excel <span className="text-brand-gold-hover">to one app.</span>
            </h2>
            <p className="text-base md:text-lg text-slate-600 mt-4 leading-relaxed">
              The same daily work, without the daily chasing.
            </p>
          </div>

          <div className="rounded-3xl border border-slate-200 overflow-hidden shadow-xl bg-white mb-10">
            <div className="hidden lg:grid grid-cols-[1fr_1.5fr_1.5fr] bg-brand-ink text-white px-6 py-4 font-cabinet font-bold text-sm tracking-wide">
              <div>WORK</div>
              <div className="text-red-300 flex items-center gap-2"><span>✕</span> TODAY (REGISTERS & EXCEL)</div>
              <div className="text-brand-gold flex items-center gap-2"><span>✓</span> WITH COACHHUB</div>
            </div>
            <div className="divide-y divide-slate-100">
              {COMPARISON_ROWS.map(({ feature, icon: Icon, oldWay, coachHubWay, winnerNote }) => (
                <div key={feature} className="grid lg:grid-cols-[1fr_1.5fr_1.5fr] px-5 py-4 sm:px-6 gap-3 lg:gap-4 items-center hover:bg-slate-50/80 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-brand-ink/5 border border-brand-ink/10 text-brand-ink flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-brand-gold-hover" />
                    </span>
                    <div>
                      <p className="font-cabinet font-black text-brand-ink text-base sm:text-lg">{feature}</p>
                    </div>
                  </div>

                  <div className="rounded-2xl bg-red-50/60 border border-red-100 px-4 py-3 text-xs sm:text-sm text-slate-700 leading-relaxed">
                    <span className="lg:hidden block font-bold text-red-600 text-xs mb-1">Today:</span>
                    {oldWay}
                  </div>

                  <div className="rounded-2xl bg-emerald-50/70 border border-emerald-200/80 px-4 py-3 text-xs sm:text-sm text-slate-800 leading-relaxed relative">
                    <span className="lg:hidden block font-bold text-emerald-700 text-xs mb-1">With CoachHub:</span>
                    <p className="mb-2">{coachHubWay}</p>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[11px] font-bold">
                      <Check className="w-3 h-3" /> {winnerNote}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Migration Guarantee Box */}
          <div className="rounded-3xl bg-gradient-to-r from-brand-ink via-brand-navy to-brand-ink text-white p-6 sm:p-8 md:p-10 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="relative z-10 max-w-xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold/20 text-brand-gold px-3 py-1 text-xs font-bold uppercase tracking-wider mb-2">
                <Zap className="w-3.5 h-3.5" /> 100% Free Data Migration
              </span>
              <h3 className="font-cabinet text-2xl sm:text-3xl font-black mb-2 text-white">
                Worried about switching from Excel or your old software?
              </h3>
              <p className="text-white/75 text-sm sm:text-base leading-relaxed">
                Send us your Excel sheets and our team imports students, batches, fee structures and pending dues for free, so you start with your own data on day one.
              </p>
            </div>
            <div className="relative z-10 flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <button
                type="button"
                onClick={() => openDemoModal('Free Data Migration Assistance')}
                className={goldBtn}
              >
                Schedule Migration Call <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── ERP types ─── */}
      <section id="erp-types" className="scroll-mt-24 py-16 md:py-20 lg:py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow>ERP types</Eyebrow>
            <h2 className="font-cabinet text-4xl md:text-5xl lg:text-6xl font-black text-brand-ink leading-[0.98] tracking-tight mb-4">
              One platform. <span className="text-brand-gold-hover">Every kind of institute.</span>
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              Pick your institute type and CoachHub sets up the right academic structure, modules and workflows for you.
            </p>
          </div>

          <div className="grid grid-cols-3 sm:flex sm:justify-center gap-2 mb-8 max-w-md sm:max-w-none mx-auto" role="tablist" aria-label="Institute type">
            {INSTITUTION_TYPES.map(({ key, short, icon: Icon }) => {
              const active = key === activeType;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveType(key)}
                  className={`inline-flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-5 py-3 rounded-xl text-[13px] sm:text-sm font-semibold transition-all ${active ? 'bg-brand-ink text-white shadow-lg shadow-brand-ink/25' : 'bg-brand-mist text-brand-ink hover:bg-slate-200'}`}
                  data-testid={`erp-type-${key}`}
                >
                  <Icon className={`w-4 h-4 ${active ? 'text-brand-gold' : ''}`} /> {short}
                </button>
              );
            })}
          </div>

          <div className="grid lg:grid-cols-[1.1fr_0.9fr] rounded-3xl overflow-hidden border border-slate-200" role="tabpanel">
            <div className="bg-brand-ink text-white p-6 sm:p-8 md:p-12 relative overflow-hidden flex flex-col">
              <div className="absolute inset-0 bg-grid-dark opacity-60" aria-hidden="true" />
              <div className="relative flex flex-col flex-1">
                <div className="w-14 h-14 rounded-2xl bg-brand-gold/15 border border-brand-gold/30 flex items-center justify-center mb-6">
                  <activeInstitution.icon className="w-6 h-6 text-brand-gold" />
                </div>
                <h3 className="font-cabinet text-3xl md:text-4xl font-black mb-4 tracking-tight">{activeInstitution.title}</h3>
                <p className="text-white/70 leading-relaxed mb-7 max-w-xl">{activeInstitution.desc}</p>
                <ul className="grid sm:grid-cols-2 gap-3 mb-8">
                  {activeInstitution.points.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-sm text-white/85">
                      <Check className="w-4 h-4 text-brand-gold flex-shrink-0 mt-0.5" /> {point}
                    </li>
                  ))}
                </ul>
                <Link to="/register" className={`${goldBtn} self-start`}>
                  Start with {activeInstitution.short} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            </div>
            <div className="bg-brand-cream p-6 sm:p-8 md:p-12 flex flex-col justify-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-hover mb-4">Modules included</p>
              <div className="flex flex-wrap gap-2">
                {activeInstitution.modules.map((module) => (
                  <span key={module} className="px-3.5 py-2 rounded-lg bg-white border border-brand-gold/25 text-sm font-semibold text-brand-ink">{module}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Mobile app ─── */}
      {(mobileApp.enabled !== false) && (
        <section id="mobile-app" className="relative py-16 md:py-20 lg:py-24 px-4 overflow-hidden bg-brand-navy" data-testid="mobile-app-section">
          <div className="absolute inset-0 bg-grid-dark opacity-60" aria-hidden="true" />
          <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Eyebrow dark>Mobile app</Eyebrow>
              <h2 className="font-cabinet text-4xl md:text-5xl font-black text-white leading-[0.98] tracking-tight mb-5">
                {mobileApp.title || 'Your institute, in every pocket.'}
              </h2>
              <p className="text-white/65 text-base md:text-lg mb-6 leading-relaxed max-w-lg">
                {mobileApp.subtitle || 'Owners, teachers, students and parents each get their own app experience — attendance, fees, results and alerts in real time.'}
              </p>

              {/* White-Label Custom Branding Highlight */}
              <div className="mb-7 p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-brand-gold/15 to-white/[0.04] border border-brand-gold/30 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-7 h-7 rounded-lg bg-brand-gold text-brand-ink flex items-center justify-center font-bold">
                    <Palette className="w-4 h-4" />
                  </span>
                  <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-brand-gold">
                    100% White-Label: Aapki Apni Branding
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs text-white/85">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-brand-gold flex-shrink-0" />
                    <span>Your Own Logo & Play Store App Name</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-brand-gold flex-shrink-0" />
                    <span>Personalized Brand Color Combination</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-brand-gold flex-shrink-0" />
                    <span>Custom Splash Screen & App Launcher Icon</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-brand-gold flex-shrink-0" />
                    <span>No Third-Party Vendor Name on Student App</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-7">
                {appRoles.map(r => {
                  const RoleIcon = ROLE_ICONS[r.role] || Users;
                  const isActive = activeAppRole === r.role;
                  return (
                    <button
                      key={r.role}
                      type="button"
                      onClick={() => setActiveAppRole(r.role)}
                      aria-pressed={isActive}
                      data-testid={`app-role-${r.role.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        isActive
                          ? 'bg-brand-gold text-brand-ink shadow-lg'
                          : 'bg-white/[0.06] text-white/70 hover:bg-white/10 hover:text-white border border-white/10'
                      }`}
                    >
                      <RoleIcon className="w-4 h-4" />
                      {r.role}
                    </button>
                  );
                })}
              </div>

              {appRoles.filter(r => r.role === activeAppRole).map(r => (
                <ul key={r.role} className="space-y-3">
                  {(r.features || []).map((feat, i) => (
                    <li key={i} className="flex items-center gap-3" data-testid={`app-feature-${i}`}>
                      <span className="w-6 h-6 rounded-lg bg-brand-gold/15 border border-brand-gold/30 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5 text-brand-gold" />
                      </span>
                      <span className="text-white/80 text-sm">{feat}</span>
                    </li>
                  ))}
                </ul>
              ))}

              <div className="mt-9 flex flex-col sm:flex-row items-start gap-3">
                <a
                  href={mobileApp.play_store_url || '#'}
                  target={mobileApp.play_store_url ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  data-testid="play-store-download-btn"
                  className="inline-flex items-center gap-3 bg-white text-brand-ink font-semibold pl-4 pr-6 py-3 rounded-xl hover:bg-brand-cream transition-all active:scale-95 shadow-lg"
                >
                  <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor" aria-hidden="true">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302a1 1 0 010 1.38l-2.302 2.302L15.4 12l2.298-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302-8.635-8.635z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-[10px] text-slate-500 leading-none uppercase tracking-wide">Get it on</p>
                    <p className="text-base font-bold leading-tight">Google Play</p>
                  </div>
                </a>
                {!mobileApp.play_store_url && (
                  <span className="text-white/40 text-xs mt-2 sm:mt-4">Coming soon on Play Store</span>
                )}
              </div>
            </div>
            <div className="flex justify-center relative">
              <AppPhones activeRole={activeAppRole} />
              {[
                { icon: Palette, title: 'Your Logo & Theme', sub: 'Custom institute colors', pos: 'top-4 right-0' },
                { icon: CreditCard, title: 'Fee paid · ₹8,500', sub: 'Receipt sent on WhatsApp', pos: 'top-28 left-0' },
                { icon: Trophy, title: 'Result published', sub: 'Physics · 92 / 100 · Rank 3', pos: 'top-[56%] right-0' },
                { icon: Bell, title: 'Class at 4:00 PM', sub: 'Batch NEET-A · Room 2', pos: 'bottom-16 left-0' },
              ].map(({ icon: Icon, title, sub, pos }) => (
                <div key={title} className={`hidden lg:flex absolute ${pos} items-center gap-2.5 rounded-2xl bg-white/95 backdrop-blur px-3 py-2.5 shadow-2xl shadow-black/30 site-pop-in z-20`}>
                  <span className="w-8 h-8 rounded-lg bg-brand-ink flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-brand-gold" /></span>
                  <span>
                    <span className="block text-[13px] font-bold text-brand-ink leading-tight">{title}</span>
                    <span className="block text-[11px] text-slate-500">{sub}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Pricing: limited-time per-student offer ─── */}
      <section id="pricing" className="scroll-mt-24 py-16 md:py-20 lg:py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="font-cabinet text-4xl md:text-5xl lg:text-6xl font-black text-brand-ink leading-[0.98] tracking-tight mb-4">
              One simple price. <span className="text-brand-gold-hover">All core modules included.</span>
            </h2>
            <p className="text-base md:text-lg text-slate-600">No setup fee. Pay only for the students you teach.</p>
          </div>

          <div className="grid lg:grid-cols-[1.05fr_0.95fr] rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-brand-ink/10">
            {/* Offer card */}
            <div className="relative bg-brand-ink text-white p-7 sm:p-10 overflow-hidden">
              <div className="absolute inset-0 bg-grid-dark opacity-60" aria-hidden="true" />
              <div className="absolute -top-24 -right-24 w-72 h-72 bg-brand-gold/25 blur-3xl rounded-full" aria-hidden="true" />
              <div className="relative">
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold text-brand-ink px-3 py-1.5 text-xs font-black uppercase tracking-[0.14em]">
                    <Zap className="w-3.5 h-3.5" /> Limited-time offer
                  </span>
                  <span className="rounded-full border border-emerald-400/40 bg-emerald-400/10 text-emerald-300 px-3 py-1.5 text-xs font-bold">Save 80%</span>
                </div>

                <p className="text-white/60 text-sm mb-2">Complete ERP · per student · per year</p>
                <div className="flex flex-wrap items-end gap-x-4 gap-y-1 mb-2">
                  <span className="font-cabinet text-3xl sm:text-4xl font-black text-white/40 line-through decoration-red-400 decoration-[3px]" aria-label="Regular price 500 rupees">₹500</span>
                  <span className="font-cabinet text-7xl sm:text-8xl font-black text-brand-gold leading-none">₹100</span>
                  <span className="text-white/60 pb-2">/ student / year</span>
                </div>
                <p className="text-sm text-white/55 mb-8">That's less than ₹9 per student per month.</p>

                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-8">
                  {['All core modules included', 'Biometric attendance + alerts', 'Fees, receipts & reminders', 'AI lead CRM & admissions', 'WhatsApp integration', 'Exams, report & admit cards', 'Mobile app for every role', 'Free setup & data migration'].map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-white/85">
                      <Check className="w-4 h-4 text-brand-gold flex-shrink-0 mt-0.5" strokeWidth={3} /> {item}
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/register" className={goldBtn} data-testid="pricing-offer-cta">
                    Claim offer — Start free trial <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                  <Link to="/#contact" className={ghostBtnDark}>Talk to sales</Link>
                </div>
                <p className="mt-4 text-xs text-white/45">14-day free trial · No card required · Offer price locked for your first year · AI credits and add-on modules billed separately</p>
              </div>
            </div>

            {/* Savings calculator */}
            <div className="bg-brand-cream p-7 sm:p-10 flex flex-col">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-hover mb-2">Savings calculator</p>
              <h3 className="font-cabinet text-2xl sm:text-3xl font-black text-brand-ink mb-6">How much will you pay?</h3>

              <label htmlFor="student-count" className="flex items-center justify-between text-sm font-semibold text-brand-ink mb-3">
                <span>Number of students</span>
                <span className="font-cabinet text-2xl font-black">{studentCount.toLocaleString('en-IN')}</span>
              </label>
              <input
                id="student-count"
                type="range"
                min="50"
                max="3000"
                step="50"
                value={studentCount}
                onChange={(e) => setStudentCount(Number(e.target.value))}
                className="w-full accent-[#E3A21A] mb-2"
                data-testid="pricing-student-slider"
              />
              <div className="flex justify-between text-xs text-slate-500 mb-8"><span>50</span><span>3,000+</span></div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between rounded-xl bg-white border border-slate-200 px-4 py-3">
                  <span className="text-sm text-slate-500">Regular price</span>
                  <span className="font-semibold text-slate-400 line-through">₹{(studentCount * 500).toLocaleString('en-IN')} / yr</span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-brand-ink text-white px-4 py-4">
                  <span className="text-sm font-semibold">You pay with offer</span>
                  <span className="font-cabinet text-2xl sm:text-3xl font-black text-brand-gold">₹{(studentCount * 100).toLocaleString('en-IN')} / yr</span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3">
                  <span className="text-sm font-semibold text-emerald-800">You save</span>
                  <span className="font-bold text-emerald-700">₹{(studentCount * 400).toLocaleString('en-IN')} / yr</span>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-brand-gold/40 bg-white px-4 py-3 mb-4">
                <Fingerprint className="w-6 h-6 text-brand-gold-hover flex-shrink-0" />
                <p className="text-sm text-brand-ink"><span className="font-bold">500+ students?</span> Get a biometric machine free. <Link to="/#contact" className="font-semibold text-brand-navy underline underline-offset-2">Ask for details</Link></p>
              </div>
              <p className="mt-auto text-xs text-slate-500">Running more than 3,000 students or multiple branches? <Link to="/#contact" className="font-semibold text-brand-navy underline underline-offset-2">Ask for an enterprise quote.</Link></p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Demo Section with On-Page Interactive Form ─── */}
      <section id="demo" className="scroll-mt-24 py-16 md:py-20 lg:py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow>Live Interactive Demo</Eyebrow>
            <h2 className="font-cabinet text-3xl sm:text-4xl md:text-5xl font-black text-brand-ink leading-[0.98] tracking-tight mb-4">
              See CoachHub in Action with <span className="text-brand-gold-hover">Your Batches & Fees</span>
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              Book a tailored 20-minute screen walkthrough. We'll set up your coaching or school structure, demonstrate biometric WhatsApp alerts live, and answer all your questions.
            </p>
          </div>

          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-14 items-start">
            {/* Left Column: Workflow + Live Metrics Preview */}
            <div className="space-y-8">
              <div>
                <h3 className="font-cabinet text-2xl font-black text-brand-ink mb-3">
                  How a Real Institute Day Runs on CoachHub
                </h3>
                <p className="text-slate-600 text-sm mb-6">
                  Every step is automated so you and your staff focus on teaching, while the software runs the operations.
                </p>
                <ol className="space-y-4">
                  {DEMO_WORKFLOW.map(({ title, desc }, idx) => (
                    <li key={title} className="flex gap-4 p-3.5 rounded-2xl hover:bg-slate-50 transition-colors">
                      <span className="w-10 h-10 rounded-xl bg-brand-ink text-brand-gold font-cabinet font-black text-base flex items-center justify-center flex-shrink-0 shadow-sm">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-bold text-brand-ink text-base">{title}</p>
                        <p className="text-xs sm:text-sm text-slate-600 leading-relaxed mt-0.5">{desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { icon: Clock, title: '20 minutes', desc: 'On a call or screen share' },
                  { icon: FileSpreadsheet, title: 'Your own data', desc: 'Share an Excel sheet, see it live' },
                  { icon: ShieldCheck, title: 'No obligation', desc: '14-day free trial after' },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="rounded-2xl border border-slate-200 bg-brand-mist px-4 py-4">
                    <Icon className="w-5 h-5 text-brand-gold-hover mb-2" />
                    <p className="font-bold text-brand-ink text-sm">{title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column: Embedded On-Page Demo Request Form */}
            <div id="demo-form" className="rounded-3xl bg-brand-cream border border-brand-gold/30 p-6 sm:p-8 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-gold/15 rounded-full blur-2xl pointer-events-none" />
              
              {onPageSubmitted ? (
                <div className="text-center py-8 space-y-4">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mx-auto flex items-center justify-center animate-bounce">
                    <CheckCircle2 className="w-10 h-10" />
                  </div>
                  <h4 className="font-cabinet text-2xl font-black text-brand-ink">
                    Demo Request Received, {onPageForm.name}!
                  </h4>
                  <p className="text-sm text-slate-600 leading-relaxed max-w-sm mx-auto">
                    Our solution specialist will reach out to you on WhatsApp at <strong className="text-brand-ink">{onPageForm.phone}</strong> to confirm your personalized walkthrough.
                  </p>
                  <div className="pt-2">
                    <a
                      href={`https://wa.me/916260129453?text=${encodeURIComponent(`Hi CoachHub team, I just requested a live demo for ${onPageForm.institute_name || 'my institute'}, ${onPageForm.city || ''}. Please schedule my walkthrough.`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold px-6 py-3.5 rounded-xl shadow-lg transition-all text-sm"
                    >
                      <MessageCircle className="w-5 h-5" /> Connect on WhatsApp Now
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOnPageSubmitted(false)}
                    className="text-xs text-slate-500 hover:text-brand-ink font-semibold underline underline-offset-2 block mx-auto pt-2"
                  >
                    Submit another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleOnPageSubmit} className="space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-gold-hover">Fast 1-on-1 Walkthrough</span>
                  </div>
                  <h3 className="font-cabinet text-2xl font-black text-brand-ink">
                    Schedule Your Free 20-Min Demo
                  </h3>
                  <p className="text-xs text-slate-500 mb-4">
                    Fill in your institute details and our solution team will set up your personalized screen-share walkthrough.
                  </p>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Director / Owner Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Rajesh Kumar (Director)"
                      value={onPageForm.name}
                      onChange={e => setOnPageForm({ ...onPageForm, name: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-brand-ink placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      WhatsApp / Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">+91</span>
                      <input
                        type="tel"
                        required
                        placeholder="98765 43210"
                        value={onPageForm.phone}
                        onChange={e => setOnPageForm({ ...onPageForm, phone: e.target.value })}
                        className="w-full rounded-xl border border-slate-200 bg-white pl-11 pr-3.5 py-2.5 text-sm text-brand-ink placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Institute / Coaching Name
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Apex Academy"
                        value={onPageForm.institute_name}
                        onChange={e => setOnPageForm({ ...onPageForm, institute_name: e.target.value })}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-brand-ink placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        City / State
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Kota, Indore"
                        value={onPageForm.city}
                        onChange={e => setOnPageForm({ ...onPageForm, city: e.target.value })}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-brand-ink placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Institute Type
                      </label>
                      <select
                        value={onPageForm.institute_type}
                        onChange={e => setOnPageForm({ ...onPageForm, institute_type: e.target.value })}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                      >
                        <option value="Coaching">Coaching Institute</option>
                        <option value="School">School (K-12)</option>
                        <option value="College">College / Degree</option>
                        <option value="Tuition">Tuition / Academy</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Student Strength
                      </label>
                      <select
                        value={onPageForm.student_count}
                        onChange={e => setOnPageForm({ ...onPageForm, student_count: e.target.value })}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                      >
                        <option value="Under 100">Under 100</option>
                        <option value="100 – 300">100 – 300</option>
                        <option value="300 – 1,000">300 – 1,000</option>
                        <option value="1,000+ (Multi-Branch)">1,000+ (Multi-Branch)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Primary Feature of Interest
                    </label>
                    <select
                      value={onPageForm.primary_requirement}
                      onChange={e => setOnPageForm({ ...onPageForm, primary_requirement: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
                    >
                      <option value="Biometric Attendance & Instant WhatsApp Alerts">Biometric Attendance + WhatsApp Alerts</option>
                      <option value="Smart Fee Management & Auto Dues Reminders">Smart Fee Management & Auto Dues Recovery</option>
                      <option value="Own Branded Mobile App & Custom Theme">Own Branded Mobile App (Your Logo & Theme)</option>
                          <option value="AI Exam Paper Generator & OMR Evaluation">AI Exam Generator & OMR Sheet Checking</option>
                      <option value="Facebook & Instagram Lead CRM">Facebook & Instagram Lead CRM</option>
                      <option value="LMS & Video Lectures Vault">LMS, Video Lectures & DPPs</option>
                      <option value="Multi-Branch & Franchise Management">Multi-Branch Central Management</option>
                      <option value="Google Reviews & Rating Booster">Google Reviews & Rating Booster</option>
                      <option value="Complete All-in-One ERP Walkthrough">Complete All-in-One ERP Walkthrough</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={onPageSubmitting}
                    className="w-full py-4 rounded-xl bg-brand-gold hover:bg-brand-gold-hover text-brand-ink font-cabinet font-black text-base shadow-xl shadow-brand-gold/30 transition-all hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70 mt-2"
                    data-testid="onpage-demo-submit-btn"
                  >
                    {onPageSubmitting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-brand-ink border-t-transparent rounded-full animate-spin" />
                        Scheduling Your Demo...
                      </>
                    ) : (
                      <>
                        Schedule 20-Min Free Demo <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
                    <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Free 14-day trial</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-brand-gold-hover" /> No credit card</span>
                    <span className="inline-flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-blue-600" /> Free data migration</span>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="scroll-mt-24 py-16 md:py-20 lg:py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Eyebrow>Common questions</Eyebrow>
            <h2 className="font-cabinet text-4xl md:text-5xl lg:text-6xl font-black text-brand-ink leading-[0.98] tracking-tight">
              Questions, <span className="text-brand-gold-hover">answered.</span>
            </h2>
          </div>
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, idx) => (
              <details key={item.question} className="group rounded-2xl border border-slate-200 bg-white open:border-brand-gold/50 open:shadow-lg open:shadow-brand-ink/5 transition-all" data-testid={`faq-item-${idx}`} open={idx === 0}>
                <summary className="flex items-center justify-between gap-3 sm:gap-4 cursor-pointer list-none px-4 sm:px-6 py-4 sm:py-5 [&::-webkit-details-marker]:hidden">
                  <span className="flex items-center gap-3 sm:gap-4">
                    <span className="font-cabinet text-lg font-black text-brand-gold-hover">{String(idx + 1).padStart(2, '0')}</span>
                    <span className="font-bold text-brand-ink md:text-lg">{item.question}</span>
                  </span>
                  <ChevronDown className="w-5 h-5 text-brand-ink flex-shrink-0 group-open:rotate-180 transition-transform" />
                </summary>
                <p className="px-4 sm:px-6 pb-5 sm:pb-6 md:pl-[4.25rem] text-sm sm:text-base text-slate-600 leading-relaxed">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Contact / final CTA ─── */}
      <section id="contact" className="scroll-mt-24 py-16 md:py-20 lg:py-24 px-4 bg-brand-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark" aria-hidden="true" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[340px] bg-brand-gold/20 blur-[120px] rounded-full" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <div>
            <Eyebrow dark>Contact us</Eyebrow>
            <h2 className="font-cabinet text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.98] tracking-tight mb-6">
              Modernise your institute <span className="text-brand-gold">this week.</span>
            </h2>
            <p className="text-white/70 text-base md:text-lg max-w-xl leading-relaxed mb-8">
              Talk to our team for a free demo, migration help from your current software or spreadsheets, and pricing for your institute.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <Link to="/register" data-testid="cta-register-btn" className={goldBtn}>
                {trialCta} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <a href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hi, I would like a demo of mycoachhub.in')}`} target="_blank" rel="noopener noreferrer" className={ghostBtnDark} data-testid="cta-whatsapp-btn">
                <MessageCircle className="w-4 h-4" /> Book demo on WhatsApp
              </a>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/55">
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-brand-gold" /> No credit card</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-brand-gold" /> Free 14-day trial</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-brand-gold" /> Free data migration</span>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/[0.05] backdrop-blur p-6 md:p-8 space-y-3">
            {[
              { icon: Phone, label: 'Call us', value: contactPhone, href: `tel:${contactPhone.replace(/\s/g, '')}` },
              { icon: Mail, label: 'Email', value: contactEmail, href: `mailto:${contactEmail}` },
              { icon: MapPin, label: 'Office', value: 'Gwalior, Madhya Pradesh, India' },
              { icon: Clock, label: 'Support hours', value: 'Mon – Sat · 10:00 AM – 7:00 PM' },
            ].map(({ icon: Icon, label, value, href }) => {
              const body = (
                <>
                  <span className="w-11 h-11 rounded-xl bg-brand-gold/15 border border-brand-gold/30 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-brand-gold" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs uppercase tracking-[0.18em] text-white/45">{label}</span>
                    <span className="block font-semibold text-white break-words">{value}</span>
                  </span>
                </>
              );
              return href ? (
                <a key={label} href={href} className="flex items-center gap-4 rounded-2xl bg-brand-ink/60 border border-white/5 px-4 py-4 hover:border-brand-gold/40 transition-colors">{body}</a>
              ) : (
                <div key={label} className="flex items-center gap-4 rounded-2xl bg-brand-ink/60 border border-white/5 px-4 py-4">{body}</div>
              );
            })}
          </div>
        </div>
      </section>

      </main>

      {/* Floating Quick Action Widget for Instant Demo / WhatsApp (positioned to the left of the AI chat FAB) */}
      <div className="fixed bottom-4 sm:bottom-5 right-[74px] sm:right-[92px] z-40 flex items-center gap-2 sm:gap-2.5">
        <button
          type="button"
          onClick={() => openDemoModal('Quick Inquiry')}
          className="flex items-center gap-2 bg-brand-gold hover:bg-brand-gold-hover text-brand-ink font-cabinet font-black px-3.5 sm:px-5 py-2.5 sm:py-3 rounded-full shadow-2xl shadow-brand-gold/50 transition-all hover:scale-105 active:scale-95 text-xs sm:text-sm cursor-pointer whitespace-nowrap"
          data-testid="floating-demo-btn"
        >
          <Sparkles className="w-4 h-4 text-brand-ink" />
          <span>Book Demo</span>
        </button>
        <a
          href="https://wa.me/916260129453?text=Hi%20CoachHub%20team%2C%20I%20am%20an%20institute%20owner%20and%20want%20a%20demo%20of%20your%20software."
          target="_blank"
          rel="noopener noreferrer"
          className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] text-white flex items-center justify-center shadow-2xl shadow-[#25D366]/40 transition-all hover:scale-110 active:scale-95 cursor-pointer flex-shrink-0"
          aria-label="Chat on WhatsApp"
          data-testid="floating-whatsapp-btn"
        >
          <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
        </a>
      </div>

      <Footer />

      <DemoEnquiryModal
        isOpen={demoModalOpen}
        onClose={() => setDemoModalOpen(false)}
        initialRequirement={demoRequirement}
      />

      {showDeferredUi && (
        <Suspense fallback={null}>
          <DeferredPublicChatWidget />
        </Suspense>
      )}
    </div>
  );
}

